import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Locale } from '@et/schema';
import { formatSpan } from '@et/schema/date';
import {
  Button, Card, CardBody, EmptyState, ErrorCard, EvidenceGlyph, Pill,
  Skeleton, StrengthTag, FallbackBadge, Timeline, witnessFamily, WITNESS_LANES, type TimelinePin,
} from '@et/ui';
import {
  fetchCharacterProfile, fetchEvidence, fetchFacets, queryKeys,
  type ContentRev, type EvidenceListItem,
} from '../../lib/api.js';
import { parseFilters, serialiseFilters, toggle, isEmpty } from '../../lib/filters.js';
import { LOCALES, toUrlSegment } from '../../lib/locale.js';
import { PageMeta } from '../../app/PageMeta.js';
import { EvidencePeek } from './EvidencePeek.js';
import { Ledger } from './Ledger.js';

/** A Antiguidade e a era das descobertas, os dois dominios do eixo dividido. */
const ANTIQUITY = [-800, 900] as const;
const DISCOVERY = [1850, 2030] as const;

const toPin = (item: EvidenceListItem): TimelinePin => ({
  id: item.id,
  title: item.title,
  category: 'evidence',
  evidenceType: item.evidenceType as TimelinePin['evidenceType'],
  event: item.event,
  discovery: item.discovery,
  disputed: item.disputed,
});

export function ExplorePage({ locale, rev }: { readonly locale: Locale; readonly rev: ContentRev }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);
  const selectedId = params.get('selected');
  const seeded = useRef(false);

  /**
   * A tradicao por omissao entra **na URL**, e nao numa variavel.
   *
   * Se ficasse so em memoria, a ausencia do parametro significaria duas coisas
   * diferentes — "ainda nao escolheste" e "escolheste nenhuma" — e desligar o
   * Cristianismo nao teria efeito nenhum, porque o valor por omissao voltava a
   * entrar. Escrito uma vez, na primeira chegada, a URL passa a dizer sempre a
   * verdade e "limpar filtros" volta a mostrar as duas tradicoes.
   */
  useEffect(() => {
    if (seeded.current || params.has('religion') || params.toString().length > 0) return;
    seeded.current = true;
    setParams(new URLSearchParams({ religion: 'christianity' }), { replace: true });
  }, [params, setParams]);

  // Escolher um pin longe da linha correspondente deixaria o segundo toque
  // fora do ecra; trazer a linha ao pe e o que torna o percurso praticavel.
  useEffect(() => {
    if (selectedId === null) return;
    document.querySelector(`[data-evidence="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  const facets = useQuery({
    queryKey: queryKeys.facets(rev, locale),
    queryFn: () => fetchFacets(rev.version, locale),
  });
  const evidence = useQuery({
    queryKey: queryKeys.evidence(rev, locale, filters),
    queryFn: () => fetchEvidence(rev.version, locale, filters),
  });

  /**
   * O periodo de vida da figura de cada tradicao escolhida.
   *
   * Vem do conteudo (`Figure.lifespan`) e nao de uma constante: acrescentar
   * uma terceira tradicao nao pode exigir editar esta pagina. Sem tradicao
   * escolhida nao aparece marca nenhuma — marcar todas de uma vez encheria o
   * eixo de faixas e nenhuma se leria.
   */
  const profile = useQuery({
    queryKey: queryKeys.characterProfile(rev, locale),
    queryFn: () => fetchCharacterProfile(rev.version, locale),
    staleTime: 60_000,
  });
  const eras = useMemo(() => (profile.data?.figures ?? [])
    .filter((f) => filters.religion.includes(f.religionId))
    .flatMap((f) => {
      const from = f.lifespan?.earliest;
      const to = f.lifespan?.latest;
      if (from === undefined || to === undefined) return [];
      return [{ id: f.id, label: f.name, from, to }];
    }), [profile.data, filters.religion]);

  const update = (next: typeof filters) => setParams(serialiseFilters(next), { replace: false });

  return (
    <div className="et-explore">
      {/*
        A raiz de cada lingua e a pagina por onde os motores percebem o que o
        sitio e.

        Sem `SearchAction`, de proposito. A caixa de pesquisa daqui e um
        typeahead que salta directamente para o resultado: nao ha nenhum
        endereco `?q=` que devolva uma lista, e declarar um ao Google para
        ganhar a caixa de pesquisa nos resultados seria apontar toda a gente
        para uma pagina que nao existe. Volta a entrar aqui no dia em que
        houver mesmo uma pagina de resultados.
      */}
      <PageMeta
        description={t('explore.siteDescription')}
        jsonLd={{
          '@type': 'WebSite',
          name: 'Evidence Timeline',
          description: t('explore.siteDescription'),
          inLanguage: LOCALES.map(toUrlSegment),
        }}
      />
      {/*
        Tres eixos diferentes, tres tratamentos diferentes.

        Estavam os tres com a mesma pilula da mesma cor, o que fazia "Islao",
        "Manuscrito" e "Antes de 325" parecerem opcoes da mesma lista — e sao
        perguntas de natureza diferente: de quem, de que especie, de quando. O
        rotulo de grupo e a forma dizem-no antes de a cor dizer.
      */}
      <div className="et-explore__filters">
        <div className="et-filterbar">
          <span className="et-filterbar__legend">{t('filter.tradition')}</span>
          <div className="et-explore__row et-filterbar__set" data-axis="tradition">
            {facets.data?.religions.map((r) => (
              <Pill
                key={r.id}
                size="sm"
                pressed={filters.religion.includes(r.id)}
                onClick={() => update({ ...filters, religion: toggle(filters.religion, r.id) })}
              >
                {r.name}
              </Pill>
            ))}
          </div>
          {/*
            O contador vive aqui, e nao numa linha sua por baixo do cartao.

            Solto, ocupava uma faixa inteira do ecra para dizer um numero, e
            ficava longe da causa: o numero e o **resultado** destes filtros, e
            e ao mexer nas pilulas que se quer ve-lo mudar.
          */}
          {evidence.data !== undefined && (
            <p className="et-explore__count" data-testid="count">
              {t('explore.count', { count: evidence.data.total })}
            </p>
          )}
        </div>

        {/*
          Os dois grupos que se fecham partilham uma linha.

          Cada um ocupava uma linha inteira do cartao para mostrar um chevron e
          duas palavras — cinquenta pixeis do primeiro ecra a dizer nada. Lado a
          lado, fechados, gastam uma; aberto, o grupo estende-se a largura toda
          e o outro desce.
        */}
        <div className="et-filterbar__pair">
        <FilterGroup
          label={t('filter.witness')}
          active={filters.type.length}
          axis="witness"
        >
          <div className="et-explore__row et-filterbar__set" data-axis="witness">
            {facets.data?.evidenceTypes.map((ty) => (
              <Pill
                key={ty.id}
                size="sm"
                dot
                tone={witnessFamily(ty.id)}
                count={ty.n}
                pressed={filters.type.includes(ty.id)}
                onClick={() => update({ ...filters, type: toggle(filters.type, ty.id) })}
              >
                {t(`lexicon:evidenceType.${ty.id}`)}
              </Pill>
            ))}
          </div>
        </FilterGroup>

        <FilterGroup
          label={t('filter.when')}
          active={(filters.before === null ? 0 : 1) + (filters.withinYears === null ? 0 : 1)}
          axis="when"
        >
          <div className="et-explore__row et-filterbar__set" data-axis="when">
            <Pill
              size="sm"
              pressed={filters.before === 325}
              onClick={() => update({ ...filters, before: filters.before === 325 ? null : 325 })}
            >
              {t('filter.beforeX')} 325
            </Pill>
            <Pill
              size="sm"
              pressed={filters.withinYears === 100}
              onClick={() => update({
                ...filters,
                withinYears: filters.withinYears === 100 ? null : 100,
                anchorYear: filters.withinYears === 100 ? null : 30,
              })}
            >
              {t('filter.withinYears', { count: 100 })}
            </Pill>
            {!isEmpty(filters) && (
              <Button size="sm" variant="quiet" onClick={() => setParams(new URLSearchParams())}>
                {t('action.clearFilters')}
              </Button>
            )}
          </div>
        </FilterGroup>
        </div>
      </div>

      {evidence.isPending && (
        <div className="et-explore__list">
          {[0, 1, 2].map((i) => (
            <Card key={i}><CardBody><Skeleton width="45%" height="18px" /></CardBody></Card>
          ))}
        </div>
      )}

      {evidence.isError && <ErrorCard onRetry={() => void evidence.refetch()} />}

      {evidence.data?.items.length === 0 && (
        <EmptyState
          actions={
            <Button size="sm" variant="ghost" onClick={() => setParams(new URLSearchParams())}>
              {t('action.clearFilters')}
            </Button>
          }
        />
      )}

      {evidence.data !== undefined && evidence.data.items.length > 0 && (
        <>
          <Ledger locale={locale} rev={rev} />
          {/*
            Grafico e lista lado a lado, e nao um por cima do outro.

            Empilhados, a lista comecava abaixo da dobra e o grafico ficava a
            falar sozinho: escolher um ponto acendia uma linha que ninguem
            estava a ver. Ao lado, os dois movem-se juntos — e e ai que a
            ligacao entre a marca no tempo e o registo se ve acontecer.
          */}
          <div className="et-explore__stage">
          <div className="et-explore__chart">
          <Timeline
            pins={evidence.data.items.map(toPin)}
            // `rows` sao agora **sub-filas dentro de cada faixa**, e nao filas
            // do grafico inteiro: as filas vem das familias de testemunho.
            // Tres chegam para a densidade do seculo II-IV sem esticar a regua
            // para fora do ecra.
            rows={3}
            eras={eras}
            antiquity={ANTIQUITY}
            discovery={DISCOVERY}
            locale={locale}
            selectedId={selectedId}
            onSelect={(id) => {
              const next = new URLSearchParams(params);
              if (id === null) next.delete('selected');
              else next.set('selected', id);
              setParams(next, { replace: true });
            }}
          />
          </div>
          <aside className="et-explore__rail">
            {/*
              A camada interior existe para nao contar para a altura da grelha.

              Assim a linha da grelha e definida pelo grafico, e a coluna da
              direita iguala-a exactamente em vez de a esticar com sessenta
              cartoes. Sem isto, ou se fixava uma altura a olho — que sobra num
              filtro e falta noutro — ou a lista mandava.
            */}
            <div className="et-explore__railInner">
            {selectedId !== null && (
              <EvidencePeek
                key={selectedId}
                id={selectedId}
                locale={locale}
                rev={rev}
                onClose={() => {
                  const next = new URLSearchParams(params);
                  next.delete('selected');
                  setParams(next, { replace: true });
                }}
              />
            )}
            {/*
              A lista tem as mesmas seccoes que o grafico, pela mesma ordem.

              Corrida, eram sessenta linhas iguais onde a familia so aparecia
              numa aresta de tres pixeis — e como a lista vem por data, o topo
              e quase todo epigrafia, portanto a coluna inteira lia-se de uma
              cor so. Agrupada, as quatro familias estao presentes assim que a
              pagina abre, e cada seccao diz quantas pecas tem: e a mesma
              informacao que as faixas do lado dao pela altura.

              Dentro de cada seccao a ordem por data mantem-se intacta.
            */}
            <ul className="et-explore__list" data-testid="evidence-list">
              {WITNESS_LANES.map((lane) => {
                const mine = evidence.data.items
                  .filter((i) => witnessFamily(i.evidenceType) === lane.family);
                if (mine.length === 0) return null;
                return (
                  <li key={lane.family} className="et-explore__group" data-family={lane.family}>
                    <h3 className="et-explore__groupHead">
                      <span className="et-explore__groupName">{t(`timeline.lane.${lane.key}`)}</span>
                      <span className="et-explore__groupCount">{mine.length}</span>
                    </h3>
                    <ul className="et-explore__groupList">
                      {mine.map((item) => (
                        <li key={item.id}>
                          <EvidenceRow
                            item={item}
                            locale={locale}
                            selected={selectedId === item.id}
                            onSelect={() => {
                              const next = new URLSearchParams(params);
                              if (selectedId === item.id) next.delete('selected');
                              else next.set('selected', item.id);
                              setParams(next, { replace: true });
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
            </div>
          </aside>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Um grupo de filtros que se fecha.
 *
 * A tradicao nao passa por aqui de proposito: e a unica pergunta que a pagina
 * faz sempre, e escondida atras de um chevron passaria a ser uma opcao
 * avancada. As outras duas — especie de testemunho e periodo — ocupavam
 * metade do ecra acima da dobra para dizer uma coisa que na maior parte das
 * visitas fica como esta.
 *
 * Fechado, o cabecalho continua a dizer quantos filtros estao activos: um
 * grupo colapsado que esconde escolhas em silencio e pior do que um grupo
 * aberto que ocupa espaco.
 */
function FilterGroup({
  label, active, axis, children,
}: {
  readonly label: string;
  readonly active: number;
  readonly axis: string;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(active > 0);
  return (
    <div className="et-filterbar" data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="et-filterbar__legend et-filterbar__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="et-filterbar__chevron" aria-hidden="true" />
        {label}
        {active > 0 && <span className="et-filterbar__badge">{active}</span>}
      </button>
      <div className="et-filterbar__panel" data-axis={axis} hidden={!open}>
        {children}
      </div>
    </div>
  );
}

/**
 * A linha da lista escolhe a peca no grafico; nao salta para a pagina.
 *
 * Era uma ligacao directa ao detalhe, e isso fazia da lista uma saida em vez
 * de um par do grafico: quem clicava saia da vista de exploracao e perdia os
 * filtros que tinha montado. Agora escolhe — o cartao ao lado abre com a
 * fotografia, e e de la que se vai para a pagina inteira, uma vez, quando se
 * quer mesmo.
 */
function EvidenceRow({
  item, locale, selected = false, onSelect,
}: {
  readonly item: EvidenceListItem;
  readonly locale: Locale;
  readonly selected?: boolean;
  readonly onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const span = { earliest: item.event[0], latest: item.event[1] };
  return (
    // A mesma cor que a marca tem no grafico. E o que permite saltar de um
    // para o outro sem ler: quatro familias, duas colunas, uma correspondencia.
    <Card selected={selected} data-evidence={item.id} data-family={witnessFamily(item.evidenceType)}>
      <CardBody>
        <div className="et-row">
          <EvidenceGlyph type={item.evidenceType as never} decorative />
          <div className="et-row__main">
            {/*
              A forca vive na linha do titulo, e as datas ficam com a largura
              toda por baixo. Com a etiqueta ao lado da linha inteira, a coluna
              das datas encolhia e "1213-1205 BC -> 1896 AD" partia em duas.
            */}
            <div className="et-row__top">
              <button
                type="button"
                className="et-row__title et-row__pick"
                aria-pressed={selected}
                onClick={onSelect}
              >
                {item.title}
              </button>
              {item.fallbackFields.includes('title') && <FallbackBadge />}
              <StrengthTag strength={item.strength as never} />
            </div>
            <div className="et-row__meta">
              {/*
                A especie de testemunho por extenso, e nao so no glifo.

                O glifo distingue os doze tipos por forma, o que resolve o
                daltonismo mas nao resolve quem ainda nao aprendeu o lexico —
                que e toda a gente na primeira visita. A palavra ensina o
                glifo; depois de aprendido, o glifo chega.
              */}
              <span className="et-row__type">{t(`lexicon:evidenceType.${item.evidenceType}`)}</span>
              <span className="et-row__date">{formatSpan(span, locale)}</span>
              {item.discovery !== null && (
                <span className="et-row__date" data-discovery="true">
                  {formatSpan({ earliest: item.discovery[0], latest: item.discovery[1] }, locale)}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
